<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  canReply: {
    type: Boolean,
    default: true,
  },
  scheduledAt: {
    type: Date,
    default: null,
  },
});

const { t } = useI18n();

const channelType = computed(() => props.inbox?.channel_type || '');

const isWhatsApp = computed(() => {
  return (
    channelType.value === 'Channel::Whatsapp' ||
    channelType.value === 'Channel::TwilioSms'
  );
});

const isInstagram = computed(() => {
  return channelType.value === 'Channel::Instagram';
});

const isFacebook = computed(() => {
  return channelType.value === 'Channel::FacebookPage';
});

const hasWindowRestriction = computed(() => {
  return isWhatsApp.value || isInstagram.value || isFacebook.value;
});

const windowExpired = computed(() => {
  return hasWindowRestriction.value && !props.canReply;
});

const isBlockedByWindow = computed(() => {
  return windowExpired.value && isInstagram.value;
});

const requiresTemplate = computed(() => {
  return windowExpired.value && isWhatsApp.value;
});

const warningMessage = computed(() => {
  if (isBlockedByWindow.value) {
    return t('SCHEDULED_MESSAGES.WINDOW_WARNING.INSTAGRAM_BLOCKED');
  }
  if (requiresTemplate.value) {
    return t('SCHEDULED_MESSAGES.WINDOW_WARNING.EXPIRED');
  }
  if (windowExpired.value) {
    return t('SCHEDULED_MESSAGES.WINDOW_WARNING.EXPIRED');
  }
  return '';
});

const warningType = computed(() => {
  if (isBlockedByWindow.value) return 'error';
  if (requiresTemplate.value) return 'warning';
  if (windowExpired.value) return 'warning';
  return '';
});

const bannerClass = computed(() => {
  if (warningType.value === 'error') {
    return 'bg-n-ruby-2 border-n-ruby-5 text-n-ruby-11';
  }
  if (warningType.value === 'warning') {
    return 'bg-n-amber-2 border-n-amber-5 text-n-amber-11';
  }
  return '';
});

const iconClass = computed(() => {
  if (warningType.value === 'error') return 'i-lucide-circle-x';
  return 'i-lucide-alert-triangle';
});

defineExpose({
  isBlockedByWindow,
  requiresTemplate,
  windowExpired,
  hasWindowRestriction,
});
</script>

<template>
  <div
    v-if="warningMessage"
    class="flex items-start gap-2 p-3 text-sm border rounded-lg"
    :class="bannerClass"
  >
    <span :class="iconClass" class="size-4 shrink-0 mt-0.5" />
    <div class="flex flex-col gap-1">
      <p class="mb-0 text-sm font-medium">{{ warningMessage }}</p>
      <p
        v-if="requiresTemplate"
        class="mb-0 text-xs opacity-80"
      >
        {{ $t('SCHEDULED_MESSAGES.WINDOW_WARNING.USE_TEMPLATE') }}
      </p>
    </div>
  </div>
</template>
